import React, { useEffect, useRef, useCallback } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";

import { IconAiOutlineDownload } from "@/utils/icons";
import { lineBreakerForChartTitle } from "@/utils";

const ChartStackedHoriBar = (props: any) => {

  const { item }: any = props;
  const chartData = item?.data;
  const legendTitle = item?.legend_title || "";
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);

  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const handleExport = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.toImageURL('png', 2).then((url: string) => {
        const link = document.createElement('a');
        link.download = `${item?.title || 'chart'}.png`;
        link.href = url;
        link.click();
      });
    }
  }, [item?.title]);

  const renderChart = useCallback(() => {

    if (!chartRef.current || !chartData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 500 ? (chartRef.current.offsetWidth - 20) : 380;
    // const chartWidth = chartRef.current.offsetWidth > 500 ? chartRef.current.offsetWidth : 400;
    // const chartHeight = chartRef.current.offsetWidth;

    const rowHeight = 45;
    const chartHeight = chartData ? new Set(chartData.map((d: any) => d.y_label)).size * rowHeight : 200;
    // const chartHeight = 600;

    const title = item?.title || "";
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": item?.description || "",
      "width": chartWidth,
      "height": chartHeight,
      "autosize": { "type": "fit", "contains": "padding" },
      "padding": { "top": 0, "bottom": 0, "left": 0, "right": 10 },
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "data": [
        {
          "name": "table",
          // "values": [{"x" : 3, "y" : 3, "c" : 1, "y_label" : "Technology", "color_key" : "#83b8d9", "type_of_partnership" : "Multilateral partnerships"}, {"x" : 38, "y" : 38, "c" : 0, "y_label" : "Technology", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 10, "y" : 10, "c" : 0, "y_label" : "Planning", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 2, "y" : 2, "c" : 1, "y_label" : "Innovation", "color_key" : "#83b8d9", "type_of_partnership" : "Multilateral partnerships"}, {"x" : 14, "y" : 14, "c" : 0, "y_label" : "Project facilitation", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 8, "y" : 8, "c" : 1, "y_label" : "Policy", "color_key" : "#83b8d9", "type_of_partnership" : "Multilateral partnerships"}, {"x" : 29, "y" : 29, "c" : 0, "y_label" : "Socio-economic impact", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 34, "y" : 34, "c" : 0, "y_label" : "Country engagement", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 18, "y" : 18, "c" : 0, "y_label" : "Data", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 4, "y" : 4, "c" : 1, "y_label" : "Finance and Investment", "color_key" : "#83b8d9", "type_of_partnership" : "Multilateral partnerships"}, {"x" : 8, "y" : 8, "c" : 1, "y_label" : "Country engagement", "color_key" : "#83b8d9", "type_of_partnership" : "Multilateral partnerships"}, {"x" : 10, "y" : 10, "c" : 0, "y_label" : "Innovation", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 3, "y" : 3, "c" : 1, "y_label" : "Socio-economic impact", "color_key" : "#83b8d9", "type_of_partnership" : "Multilateral partnerships"}, {"x" : 30, "y" : 30, "c" : 0, "y_label" : "Finance and Investment", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 40, "y" : 40, "c" : 0, "y_label" : "Policy", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}, {"x" : 15, "y" : 15, "c" : 0, "y_label" : "Outlook", "color_key" : "#c8dded", "type_of_partnership" : "Bilateral partnerships"}],
          values: chartData
        },
        {
          "name": "color_mapping",
          "source": "table",
          "transform": [
            { "type": "aggregate", "groupby": ["type_of_partnership", "color_key"] }
          ]
        },
        {
          "name": "stacked_data",
          "source": "table",
          "transform": [
            {
              "type": "stack",
              "groupby": ["y_label"],
              "sort": {"field": "c"},
              "field": "y",
              "as": ["y0", "y1"]
            },
            {
              "type": "formula",
              "as": "center",
              "expr": "(datum.y0 + datum.y1) / 2"
            }
          ]
        },
        {
          "name": "totals",
          "source": "table",
          "transform": [
            {
              "type": "aggregate",
              "groupby": ["y_label"],
              "ops": ["sum"],
              "fields": ["y"],
              "as": ["total"]
            }
          ]
        }
      ],
      "legends": [
        {
          "fill": "color",
          "title": legendTitle || "",
          "orient": "right",
          "titleFontSize": 10,
          "titleFontFamily": "Arial",
          "titleColor": "black",
          "padding": 0,
          "encode": {
            "labels": {
              "update": {
                // label_cosmetic_controls
                "opacity": { "value": 1 },
                "fontSize": { "value": 10 },
                "fontFamily": { "value": "Arial" },
                "fill": { "value": "black" },
              }
            }
          }
        }
      ],
      "scales": [
        {
          "name": "y",
          "type": "band",
          "range": "height",
          "domain": {"data": "table", "field": "y_label"},
          "padding": 0.2
        },
        {
          "name": "x",
          "type": "linear",
          "range": "width",
          "nice": true, "zero": true,
          "domain": {"data": "totals", "field": "total"},
          // "domainMax": 180
        },
        {
          "name": "color",
          "type": "ordinal",
          "domain": {"data": "color_mapping", "field": "type_of_partnership"},
          "range": {"data": "color_mapping", "field": "color_key"}
        }
      ],
    
      "axes": [
        {"orient": "left", "scale": "y", "labelPadding": 10},
        {"orient": "bottom", "scale": "x", "title": "Total Value"}
      ],
    
      "marks": [
        {
          "type": "rect",
          "from": {"data": "stacked_data"},
          "encode": {
            "enter": {
              "y": {"scale": "y", "field": "y_label"},
              "height": {"scale": "y", "band": 1},
              "x": {"scale": "x", "field": "y0"},
              "x2": {"scale": "x", "field": "y1"},
              "fill": {"scale": "color", "field": "type_of_partnership"}
            },
            "update": {
              "fillOpacity": {"value": 1}
            },
            "hover": {
              "fillOpacity": {"value": 0.5}
            }
          }
        },
        {
          "type": "text",
          "from": {"data": "stacked_data"},
          "interactive": false,
          "encode": {
            "enter": {
              "y": {"scale": "y", "field": "y_label", "band": 0.5},
              "x": {"scale": "x", "field": "center"},
              "text": {"field": "y"},
              "fill": {"value": "white"},
              "align": {"value": "center"},
              "baseline": {"value": "middle"}
            }
          }
        },
        {
          "type": "text",
          "from": {"data": "totals"},
          "interactive": false,
          "encode": {
            "enter": {
              "y": {"scale": "y", "field": "y_label", "band": 0.5},
              "x": {"scale": "x", "field": "total", "offset": 8},
              "text": {"field": "total"},
              "fill": {"value": "black"},
              "align": {"value": "left"},
              "baseline": {"value": "middle"},
              "fontWeight": {"value": "bold"}
            }
          }
        }
      ]
    }

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      }
    }).then((vegaEmbedResult: any) => {
      viewRef.current = vegaEmbedResult.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [item]);


  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);


  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);

  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          onClick={handleExport}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>
      <div id="chart-grouped-bar" className="!p-0 !pr-0 !pe-0 " ref={chartRef}></div>
    </div>
  );
}

export default ChartStackedHoriBar;